import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Send, FileText, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

// Même map que ONBOARDING_DOC_TYPES / DOCUMENT_TYPES côté back
const DOC_TYPES = [
  { value: 'kbis',      label: 'Extrait Kbis',                       hint: 'moins de 3 mois' },
  { value: 'urssaf',    label: 'Attestation de vigilance URSSAF',    hint: 'moins de 3 mois' },
  { value: 'identite',  label: "Pièce d'identité du représentant",   hint: 'en cours de validité' },
  { value: 'fiscale',   label: 'Attestation de régularité fiscale',  hint: 'moins de 3 mois' },
  { value: 'decennale', label: 'Attestation Décennale / RC Pro',     hint: 'en cours' },
];

/**
 * Modal « Demander des documents complémentaires ».
 * Permet de cocher les documents à redemander au sous-traitant (ceux qui ne
 * sont pas conformes) et d'ajouter un message personnalisé optionnel.
 * Génère un nouveau lien d'upload n'affichant QUE les docs cochés.
 */
export default function RequestAdditionalDocsDialog({
  open,
  onOpenChange,
  stId,
  stNom,
  stEmail,
  uploadedDocTypes = [], // liste des doc_types déjà déposés (pour l'indicateur visuel)
  onSent,
}) {
  const [selected, setSelected] = useState(new Set());
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setCustomMessage('');
    }
  }, [open]);

  const uploadedSet = useMemo(() => new Set(uploadedDocTypes || []), [uploadedDocTypes]);

  const toggle = (dt) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(dt)) next.delete(dt); else next.add(dt);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selected.size === 0) {
      toast.error('Cochez au moins un document à redemander');
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/sous-traitants/${stId}/documents/send-renewal`, {
        doc_types: Array.from(selected),
        custom_message: customMessage.trim() || null,
      });
      toast.success(
        `Email envoyé à ${r.data.sent_to} · ${r.data.doc_types.length} document(s) demandé(s)`,
        { duration: 7000 },
      );
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi", { duration: 8000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="request-additional-docs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-600" />
            Demander des documents complémentaires
          </DialogTitle>
          <DialogDescription>
            Cochez les documents à redemander{stNom ? <> à <b>{stNom}</b></> : ''}. Un email contenant
            uniquement ces documents sera envoyé
            {stEmail ? <> à <span className="font-mono text-xs">{stEmail}</span></> : ' au sous-traitant'}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Checkboxes */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-800">
              Documents non conformes à redemander
            </Label>
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
              {DOC_TYPES.map((d) => {
                const checked = selected.has(d.value);
                const alreadyReceived = uploadedSet.has(d.value);
                return (
                  <label
                    key={d.value}
                    htmlFor={`req-doc-${d.value}`}
                    className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors"
                    data-testid={`req-doc-row-${d.value}`}
                  >
                    <Checkbox
                      id={`req-doc-${d.value}`}
                      checked={checked}
                      onCheckedChange={() => toggle(d.value)}
                      className="mt-0.5"
                      data-testid={`req-doc-checkbox-${d.value}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="font-medium text-sm text-slate-800">{d.label}</span>
                        {alreadyReceived ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded-full">
                            <CheckCircle2 className="w-3 h-3" /> Reçu
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full">
                            <AlertTriangle className="w-3 h-3" /> Jamais reçu
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{d.hint}</p>
                    </div>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-slate-500">
              {selected.size} document{selected.size > 1 ? 's' : ''} sélectionné{selected.size > 1 ? 's' : ''}
              {' · '}
              Les versions précédentes sont conservées dans l&apos;historique.
            </p>
          </div>

          {/* Custom message */}
          <div className="space-y-2">
            <Label htmlFor="req-custom-message" className="text-sm font-semibold text-slate-800">
              Message personnalisé <span className="text-slate-400 font-normal">(optionnel)</span>
            </Label>
            <Textarea
              id="req-custom-message"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Ex: Votre Kbis date de plus de 3 mois, merci d'en fournir un plus récent."
              rows={4}
              maxLength={1000}
              data-testid="req-custom-message-input"
              className="resize-none"
            />
            <p className="text-xs text-slate-500">
              Ce message sera affiché en évidence dans l&apos;email et sur la page de dépôt.
              ({customMessage.length}/1000)
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            data-testid="req-cancel-btn"
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={sending || selected.size === 0}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="req-submit-btn"
          >
            {sending
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi…</>
              : <><Send className="w-4 h-4 mr-2" /> Envoyer la demande</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
